import * as d3 from "d3";

import { timeframeToRange } from "src/organization/pages/project/environment/metrics/components/Chart/settings";
import { ViewportContextType } from "src/organization/pages/project/environment/metrics/components/ViewPortOnly/types";
import useInViewport from "src/organization/pages/project/environment/metrics/components/ViewPortOnly/useInViewport";
import {
  CursorDataIndexFuncProp,
  CursorDataType,
  LayoutDimension,
  PositionType,
  CursorDataSyncType,
  AdaptedCursor
} from "src/organization/pages/project/environment/metrics/http2/types";
import { TimeFrameType } from "src/organization/pages/project/environment/metrics/resources/types";
import { useCursorWorker } from "src/organization/pages/project/environment/metrics/utils/workers/cursorworker/useCursorWorker";

export const move = (x: number, y: number) => {
  return `translate(${x}, ${y})`;
};

export const getLayoutPositions = (dms: LayoutDimension): PositionType => ({
  xRangeStart: dms.left,
  xRangeEnd: (dms?.width || 0) - dms.right,
  yRangeEnd: dms.bottom,
  yRangeStart: dms.height - dms.bottom,
  xTransform: move(0, dms.height - dms.bottom),
  yTransform: move(dms.left, dms.top),
  width: dms.width || 400,
  height: dms.height
});

type LinearAxisGeneratorProps = {
  xDomain: Array<any>;
  yDomain: Array<any>;
  dimension: LayoutDimension;
};

export const getLinearAxis = (props: LinearAxisGeneratorProps) => {
  const { xDomain, yDomain, dimension } = props;

  const positions = getLayoutPositions(dimension);

  const xScale = d3
    .scaleTime()
    .domain(xDomain)
    .range([positions.xRangeStart, positions.xRangeEnd]);

  const yScale = d3
    .scaleLinear()
    .domain(yDomain)
    .range([positions.yRangeStart, positions.yRangeEnd]);

  return {
    yScale,
    xScale,
    positions
  };
};

export const generateLabelProgression = (
  progressionCount: number,
  maxPoint: number = 0
) => {
  const totalDistance = maxPoint;
  const commonDifference = totalDistance / (progressionCount - 1);
  const generatedProgression: number[] = [];
  let currentValue = 0;

  for (let i = 0; i < progressionCount; i++) {
    generatedProgression.push(currentValue);
    currentValue += commonDifference;
  }

  return generatedProgression;
};

// Returns a function that can be used to get cursor data from mouse events
export const getCursorDataFunc = <D>(props: CursorDataIndexFuncProp<D>) => {
  const {
    data,
    bisectFn,
    yScale,
    xScale,
    setCursorData,
    disable,
    chartId,
    syncGroup
  } = props;

  const handleMouseMove = event => {
    if (!event) {
      setCursorData?.(null);
      return;
    }

    let activityId;
    if (event.target.classList.contains("activity-line")) {
      //Dont show tooltip when hovered on activity overlay
      activityId = event.target.getAttribute("data-activityId");
    }
    if (!event.target.classList.contains("chart-graph")) {
      //Dont show tooltip when hovered on activity overlay
    }

    const [relativeX, relativeY] = d3.pointer(event);
    const [absoluteX, absoluteY] = d3.pointer(event, document.body);
    const bisect = d3.bisector(bisectFn).center;
    const yInvert = yScale.invert(relativeY);
    const xInvert = xScale.invert(relativeX);
    const dataIndex = bisect(data, xInvert);
    const indexedData = data[dataIndex];
    const cursorYpercent = (yInvert / yScale.domain()[1]) * 100;

    setCursorData?.({
      cursorYpercent,
      activityId,
      absoluteX,
      absoluteY,
      yInvert,
      xInvert,
      dataIndex,
      indexedData,
      relativeX,
      relativeY,
      chartId,
      syncGroup
    });
  };
  return disable ? undefined : handleMouseMove;
};

/**
 * Scales the cursor's Y percentage to a position within the Y scale range.
 *
 * @param cursorYpercent - The cursor's Y position as a percentage
 * @param yScale - D3's linear scale for Y axis
 * @returns The scaled Y position value
 */
const scaleCursorYpercent = (
  cursorYpercent: number,
  yScale: d3.ScaleLinear<number, number>
) => yScale.range()[0] * (1 - cursorYpercent / 100);

// Syncs the position of the overlay with the cursor cursorYpercent data from the leader chart
// This is used to ensure that the overlay is positioned correctly on the chart
// when the cursor is moved across different charts
const syncAdaptedPosition = (
  adaptedCursor: AdaptedCursor,
  chartRect?: DOMRect
): AdaptedCursor => {
  const { relativeX, relativeY } = adaptedCursor;
  if (!chartRect) return adaptedCursor;

  const absoluteY = chartRect.top + window.scrollY + relativeY;
  const absoluteX = chartRect.left + window.scrollX + relativeX;

  return {
    ...adaptedCursor,
    absoluteX,
    absoluteY
  };
};

const getIsInSyncGroup = (cursorGroup?: string[], syncGroup?: string[]) => {
  //if any of the graph belongs to a group then sync strictly with that group
  //if no group is defined then sync with all the graphs
  const hasGroup = cursorGroup || syncGroup;
  if (hasGroup) return cursorGroup?.some(g => syncGroup?.includes(g));
  return true;
};

export const adaptCursorData = <D>(props: {
  cursorFuncProps: CursorDataIndexFuncProp<D>;
  cursorData?: CursorDataType;
  chartRect?: DOMRect;
  inViewport?: ViewportContextType;
}): CursorDataSyncType | undefined => {
  const { data, bisectFn, disable, chartId, xScale, yScale, syncGroup } =
    props.cursorFuncProps;
  if (disable || !props.cursorData) return;
  const { cursorData, chartRect, inViewport } = props;
  const { xInvert, cursorYpercent, activityId } = cursorData;

  const isInCursorGroup = getIsInSyncGroup(cursorData.syncGroup, syncGroup);

  const isLeader = cursorData.chartId === chartId;

  const relativeY = scaleCursorYpercent(cursorYpercent, yScale);

  const bisect = d3.bisector(bisectFn).center;
  const dataIndex = bisect(data, xInvert);
  const indexedData = data[dataIndex];
  const relativeX = xScale(xInvert);
  const yInvert = yScale.invert(relativeY);

  const { hasProvider, visible } = inViewport || {};
  const canSyncVisible = hasProvider ? !!visible : true;

  // If it's not the leader and there's no chart rectangle, adapting the cursor position won't be useful.
  // Without a valid chart rectangle, all synchronized chart tooltips would cluster around the originating cursor event.
  const positionTooltip = isLeader || !!chartRect;
  const tooltipIsPositionable =
    positionTooltip && !!isInCursorGroup && canSyncVisible;

  const adaptCursor: AdaptedCursor = {
    absoluteX: cursorData.absoluteX,
    absoluteY: cursorData.absoluteY,
    //above will be overridden by syncAdaptedPosition function if chartRect is not defined
    chartId,
    relativeX,
    dataIndex,
    indexedData,
    cursorYpercent,
    relativeY,
    yInvert,
    //xinvert assumes that the xScale can be related to the this charts x range
    xInvert,
    activityId,
    isInCursorGroup,
    syncGroup,
    tooltipIsPositionable,
    canSyncVisible
  };

  const adaptedCursor = syncAdaptedPosition(adaptCursor, chartRect);

  return {
    adaptedCursor,
    chartRect,
    cursorData,
    isLeader,
    cursorYpercent
  };
};

export const useCursorDataSync = <D>(props: {
  cursorFuncProps: CursorDataIndexFuncProp<D>;
  enableCursorSync?: boolean;
  cursorData?: CursorDataType;
}) => {
  //if enableCursorSync is false, ensure that the tooltip is not rendered
  //else all the tooltips will be rendered in the same position
  const { cursorFuncProps, enableCursorSync = true } = props;
  const { handleCursorMove, cursorData } = useCursorWorker(cursorFuncProps);
  const inViewport = useInViewport();

  let chartRect: DOMRect | undefined;

  if (enableCursorSync) {
    // TODO move to adaptedCursor and disable with canSyncVisible and syncGroup
    // for improved performance
    const chartId = cursorFuncProps?.chartId || "";
    const chartContainer = document.getElementById(`${chartId}-chart`);
    chartRect = chartContainer?.getBoundingClientRect?.();
  }

  const cursorSync = adaptCursorData({
    inViewport,
    cursorData: props.cursorData || cursorData,
    cursorFuncProps,
    chartRect
  });

  return {
    cursorSync,
    handleCursorMove
  };
};

export const formatPercent = (value: number, total: number) =>
  value ? `${((value / total) * 100).toFixed(1)}%` : "0.0%";

/**
 * Formats a number with optional abbreviation.
 *
 * @param value - The number to format.
 * @param precision - The number of decimal places to include.
 * @param {boolean} abbreviateK - Whether to abbreviate thousands with 'k'.
 * @returns The formatted number as a string.
 */
export const formatNumber = ({
  value = 0,
  precision = 0,
  abbreviateK = true
}: {
  value?: number;
  precision?: number;
  abbreviateK?: boolean;
}) => {
  if (abbreviateK && value >= 1000) {
    return `${(value / 1000).toFixed(precision)}k`;
  }
  return precision !== undefined ? value.toFixed(precision) : value.toString();
};

export const getDefaultDomain = (
  timeframe?: TimeFrameType,
  defaultYMax = 5
) => {
  const range = timeframeToRange(timeframe);
  return { yDomain: [0, defaultYMax], xDomain: [range.start, range.end] };
};

/**
 * Returns the next number that is a multiple of the given divisor.
 * If the number is already a multiple, it returns the same number.
 *
 * @param num - The input number.
 * @param divisor - The divisor to find the next multiple of.
 * @returns The next multiple of the divisor that is greater than or equal to num.
 */
export const getNextDivisible = (num: number, divisor: number) => {
  return Math.ceil(num / divisor) * divisor;
};
